import { useState, useEffect } from 'react';

export interface BeforeAfterCase {
  id: string;
  doctorId: string;
  clinicId: string;
  clinicName: string;
  title: string;
  description: string;
  beforeImage: string;
  afterImage: string;
  isPublic: boolean;
  treatmentType: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GalleryCase {
  id: string;
  doctorId: string;
  clinicId: string;
  clinicName: string;
  title: string;
  description: string;
  beforeImage: string;
  afterImage: string;
  isPublic: boolean;
  treatmentType: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GalleryFilters {
  doctorId?: string;
  clinicId?: string;
  treatmentType?: string;
  isPublic?: boolean;
  searchQuery?: string;
  visibility: 'all' | 'public' | 'private';
  sortBy?: 'date' | 'title' | 'clinic';
  sortDirection?: 'asc' | 'desc';
}

// Mock data for the gallery
const mockCases: BeforeAfterCase[] = [
  {
    id: '1',
    doctorId: 'dr-1',
    clinicId: 'clinic-1',
    clinicName: 'Dental Excellence',
    title: 'Full Smile Makeover',
    description: 'Complete transformation with veneers on upper and lower teeth.',
    beforeImage: 'https://via.placeholder.com/500x300/FF5733/FFFFFF?text=Before',
    afterImage: 'https://via.placeholder.com/500x300/33FF57/FFFFFF?text=After',
    isPublic: true,
    treatmentType: 'Cosmetic',
    createdAt: '2025-03-15T10:30:00Z',
    updatedAt: '2025-03-15T14:15:00Z'
  },
  {
    id: '2',
    doctorId: 'dr-1',
    clinicId: 'clinic-2',
    clinicName: 'Beverly Hills Dentistry',
    title: 'Dental Implant',
    description: 'Single tooth implant with crown.',
    beforeImage: 'https://via.placeholder.com/500x300/5733FF/FFFFFF?text=Before',
    afterImage: 'https://via.placeholder.com/500x300/FF33A8/FFFFFF?text=After',
    isPublic: false,
    treatmentType: 'Restorative',
    createdAt: '2025-03-10T09:45:00Z',
    updatedAt: '2025-03-10T13:30:00Z'
  },
  {
    id: '3',
    doctorId: 'dr-2',
    clinicId: 'clinic-1',
    clinicName: 'Dental Excellence',
    title: 'Invisalign Treatment',
    description: '14-month Invisalign treatment for crowded teeth.',
    beforeImage: 'https://via.placeholder.com/500x300/33A8FF/FFFFFF?text=Before',
    afterImage: 'https://via.placeholder.com/500x300/A8FF33/FFFFFF?text=After',
    isPublic: true,
    treatmentType: 'Orthodontic',
    createdAt: '2025-02-25T11:15:00Z',
    updatedAt: '2025-02-25T15:45:00Z'
  }
];

const mockClinics = [
  { id: 'clinic-1', name: 'Dental Excellence' },
  { id: 'clinic-2', name: 'Beverly Hills Dentistry' },
  { id: 'clinic-3', name: 'Modern Dental Care' },
];

const mockTreatmentTypes = ['Cosmetic', 'Restorative', 'Orthodontic', 'Periodontic', 'Implants'];

export const useBeforeAfterGallery = () => {
  const [cases, setCases] = useState<BeforeAfterCase[]>(mockCases);
  const [loading, setLoading] = useState(false);
  const [gallery, setGallery] = useState({
    publicCount: mockCases.filter(c => c.isPublic).length,
    privateCount: mockCases.filter(c => !c.isPublic).length,
    totalCount: mockCases.length
  });
  
  const [filters, setFilters] = useState<GalleryFilters>({
    visibility: 'all',
    sortBy: 'date',
    sortDirection: 'desc'
  });

  // Add a new case
  const addCase = (caseData: Omit<BeforeAfterCase, 'id' | 'doctorId' | 'createdAt' | 'updatedAt'>) => {
    const newCase: BeforeAfterCase = {
      ...caseData,
      id: `case-${Date.now()}`,
      doctorId: 'dr-1', // Using a placeholder doctor ID
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setCases(prev => [newCase, ...prev]);
    
    // Update gallery counts
    setGallery(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1,
      publicCount: newCase.isPublic ? prev.publicCount + 1 : prev.publicCount,
      privateCount: !newCase.isPublic ? prev.privateCount + 1 : prev.privateCount
    }));
  };

  // Update an existing case
  const updateCase = (caseId: string, caseData: Partial<BeforeAfterCase>) => {
    setCases(prev => {
      const oldCases = [...prev];
      const caseIndex = oldCases.findIndex(c => c.id === caseId);
      
      if (caseIndex >= 0) {
        // Keep track of whether visibility changed
        const wasPublic = oldCases[caseIndex].isPublic;
        const willBePublic = caseData.isPublic !== undefined ? caseData.isPublic : wasPublic;
        const visibilityChanged = wasPublic !== willBePublic;
        
        // Update the case
        oldCases[caseIndex] = {
          ...oldCases[caseIndex],
          ...caseData,
          updatedAt: new Date().toISOString()
        };
        
        // Update gallery counts if visibility changed
        if (visibilityChanged) {
          setGallery(prev => ({
            ...prev,
            publicCount: willBePublic ? prev.publicCount + 1 : prev.publicCount - 1,
            privateCount: !willBePublic ? prev.privateCount + 1 : prev.privateCount - 1
          }));
        }
      }
      
      return oldCases;
    });
  };

  // Delete a case
  const deleteCase = (caseId: string) => {
    setCases(prev => {
      const caseToDelete = prev.find(c => c.id === caseId);
      const remainingCases = prev.filter(c => c.id !== caseId);
      
      // Update gallery counts
      if (caseToDelete) {
        setGallery(prev => ({
          ...prev,
          totalCount: prev.totalCount - 1,
          publicCount: caseToDelete.isPublic ? prev.publicCount - 1 : prev.publicCount,
          privateCount: !caseToDelete.isPublic ? prev.privateCount - 1 : prev.privateCount
        }));
      }
      
      return remainingCases;
    });
  };

  // Toggle visibility of a case
  const toggleVisibility = (caseId: string) => {
    setCases(prev => {
      const oldCases = [...prev];
      const caseIndex = oldCases.findIndex(c => c.id === caseId);
      
      if (caseIndex >= 0) {
        // Toggle the isPublic flag
        oldCases[caseIndex] = {
          ...oldCases[caseIndex],
          isPublic: !oldCases[caseIndex].isPublic,
          updatedAt: new Date().toISOString()
        };
        
        // Update gallery counts
        const isNowPublic = oldCases[caseIndex].isPublic;
        setGallery(prev => ({
          ...prev,
          publicCount: isNowPublic ? prev.publicCount + 1 : prev.publicCount - 1,
          privateCount: !isNowPublic ? prev.privateCount + 1 : prev.privateCount - 1
        }));
      }
      
      return oldCases;
    });
  };

  // Simulate loading cases from API
  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      // Filter cases based on filters
      let filteredCases = [...mockCases];
      
      // Filter by visibility
      if (filters.visibility === 'public') {
        filteredCases = filteredCases.filter(c => c.isPublic);
      } else if (filters.visibility === 'private') {
        filteredCases = filteredCases.filter(c => !c.isPublic);
      }
      
      // Filter by clinic
      if (filters.clinicId) {
        filteredCases = filteredCases.filter(c => c.clinicId === filters.clinicId);
      }
      
      // Filter by treatment type
      if (filters.treatmentType) {
        filteredCases = filteredCases.filter(c => 
          c.treatmentType.toLowerCase() === filters.treatmentType?.toLowerCase()
        );
      }
      
      // Filter by search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        filteredCases = filteredCases.filter(c => 
          c.title.toLowerCase().includes(query) || 
          c.description.toLowerCase().includes(query) ||
          c.clinicName.toLowerCase().includes(query)
        );
      }
      
      // Sort cases
      if (filters.sortBy) {
        filteredCases.sort((a, b) => {
          let compareValueA, compareValueB;
          
          switch (filters.sortBy) {
            case 'date':
              compareValueA = a.createdAt || '';
              compareValueB = b.createdAt || '';
              break;
            case 'title':
              compareValueA = a.title.toLowerCase();
              compareValueB = b.title.toLowerCase();
              break;
            case 'clinic':
              compareValueA = a.clinicName.toLowerCase();
              compareValueB = b.clinicName.toLowerCase();
              break;
            default:
              compareValueA = a.createdAt || '';
              compareValueB = b.createdAt || '';
          }
          
          if (filters.sortDirection === 'asc') {
            return compareValueA.localeCompare(compareValueB);
          } else {
            return compareValueB.localeCompare(compareValueA);
          }
        });
      }
      
      setCases(filteredCases);
      setLoading(false);
    }, 500);
  }, [filters]);

  return {
    gallery,
    cases,
    loading,
    filters,
    setFilters,
    addCase,
    updateCase,
    deleteCase,
    toggleVisibility,
    clinics: mockClinics,
    treatmentTypes: mockTreatmentTypes
  };
};
